/**
 * Account invoices: invoices raised from arrangements, shown on their own page
 * rather than the quote/workspace document page.
 */
import { addDays, format, parseISO } from 'date-fns';
import type { VatTreatment } from './arrangements.model';

export type AccountInvoiceStatus = 'draft' | 'sent' | 'paid' | 'void';

export const ACCOUNT_INVOICE_STATUS_LABELS: Record<AccountInvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  void: 'Void',
};

export type AccountInvoiceLine = {
  id: string;
  name: string;
  description: string | null;
  qty: number;
  /** Ex VAT. */
  net: number;
};

export type AccountInvoiceView = {
  id: string;
  invoiceRef: string;
  lifecycleRef: string;
  title: string;
  status: AccountInvoiceStatus;
  /** yyyy-MM-dd */
  issueDate: string;
  paymentTermsInDays: number;
  /** yyyy-MM-dd */
  dueDate: string;
  vatTreatment: VatTreatment;
  vatPct: number;
  netTotal: number;
  vatAmount: number;
  grossTotal: number;
  cisDeduction: number | null;
  cisLabourNet: number | null;
  notes: string | null;
  lines: AccountInvoiceLine[];
  freeagentInvoiceUrl: string | null;
  sourceEvent: string | null;
};

export function normaliseInvoiceStatus(value: unknown): AccountInvoiceStatus {
  const status = String(value || '').toLowerCase();
  if (status === 'sent' || status === 'paid' || status === 'void') return status;
  if (status === 'rejected') return 'void';
  return 'draft';
}

/** Issue date is the saved issue date, or the day it was created. */
export function invoiceIssueDate(issuedAt: string | null | undefined, createdAt: string | null | undefined): string {
  const raw = String(issuedAt || createdAt || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : format(new Date(), 'yyyy-MM-dd');
}

export function invoiceDueDate(issueDate: string, paymentTermsInDays: number): string {
  return format(addDays(parseISO(issueDate), Math.max(0, Math.floor(paymentTermsInDays || 0))), 'yyyy-MM-dd');
}

export function displayDate(isoDate: string): string {
  return format(parseISO(isoDate), 'd MMMM yyyy');
}

/** Which actions make sense for an invoice in this status. */
export function allowedInvoiceActions(status: AccountInvoiceStatus): {
  edit: boolean;
  delete: boolean;
  markSent: boolean;
  markPaid: boolean;
  void: boolean;
} {
  return {
    edit: status === 'draft',
    delete: status === 'draft',
    markSent: status === 'draft',
    markPaid: status === 'sent',
    void: status === 'sent',
  };
}

/** The period index of a recurring arrangement invoice, from its source marker. */
export function recurringPeriodIndex(sourceEvent: string | null | undefined): number | null {
  const parts = String(sourceEvent || '').split(':');
  if (parts[0] !== 'arrangement' || parts[2] !== 'recurring') return null;
  const index = Number(parts[3]);
  return Number.isInteger(index) && index >= 0 ? index : null;
}

/** Account invoice numbers can be overridden until the invoice is paid, void or in FreeAgent. */
export function canChangeInvoiceRef(status: AccountInvoiceStatus, freeagentInvoiceUrl: string | null | undefined): boolean {
  return (status === 'draft' || status === 'sent') && !freeagentInvoiceUrl;
}

/** Returns the first problem with a typed invoice number, or null. */
export function validateInvoiceRef(value: string): string | null {
  const ref = value.trim();
  if (!ref) return 'Enter an invoice number.';
  if (ref.length > 40) return 'Keep the invoice number to 40 characters or fewer.';
  if (!/^[A-Za-z0-9][A-Za-z0-9 _./-]*$/.test(ref)) return 'Use letters, numbers, spaces and - _ . / only.';
  return null;
}
